/**
 * "Is this page part of the HOTEL DEMO?" — answered from the PAGE, not from a
 * per-tab flag.
 *
 * The role switcher used to render only when `sessionStorage.yulontap_hotel_demo`
 * was '1', which only the /demo/hotel/* launcher sets. Tapping the hotel demo
 * card, following a link from the portal, or opening the demo in a fresh tab
 * (iPad Safari does this constantly) left the flag unset — so the presenter was
 * stranded in whichever role they landed in, with no way back.
 *
 * Identity now comes from the ROUTE: the demo card id in the path, or the demo
 * org on the Command Center. That is the same thing the property demo does
 * (ShowroomBackButton keys off `CONDOCARD-a-0001` in the path). The old flag is
 * still honoured as one more way in, never as the only one.
 *
 * A real hotel client can never match: their cards are not the demo card and
 * their org is not the demo org.
 */
// The session boundary: the demo session lives in sessionStorage and reading it
// here must NEVER touch the real admin session in localStorage.
import { getStoredUser } from './session';

export const HOTEL_DEMO_ORG_ID = 'hotel-demo-org-0001';
// Offline-safe fallback. The live list comes from GET /api/demo-hotel/identity
// so this can never drift if the fixture gains a second card.
const FALLBACK_CARDS = ['HARBOURVIEW1'];
const CARDS_KEY = 'yulontap_hotel_demo_cards';

const ls = () => {
    try { return window.localStorage; } catch (_e) { return null; }
};

/** Known hotel demo card ids (localStorage so a FRESH TAB already knows them). */
export const hotelDemoCards = () => {
    try {
        const raw = ls() && ls().getItem(CARDS_KEY);
        const arr = raw ? JSON.parse(raw) : null;
        if (Array.isArray(arr) && arr.length) return arr.map(String);
    } catch (_e) { /* fall through */ }
    return FALLBACK_CARDS;
};

/** Refresh the card list from the backend. Cheap, cached, never blocks render. */
export const refreshHotelDemoCards = async () => {
    const API = (typeof window !== 'undefined' ? window.location.origin : '');
    const res = await fetch(`${API}/api/demo-hotel/identity`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const d = await res.json();
    const cards = Array.isArray(d.card_ids) && d.card_ids.length ? d.card_ids.map(String) : FALLBACK_CARDS;
    try { if (ls()) ls().setItem(CARDS_KEY, JSON.stringify(cards)); } catch (_e) { /* ignore */ }
    return cards;
};

/** The card id out of /assistant/:id, /assistant/staff/:id or /hotel/:id. */
export const routeCardId = (pathname = '') => {
    const m = /^\/(?:assistant\/staff|assistant|hotel)\/([^/?#]+)/.exec(pathname);
    return m ? decodeURIComponent(m[1]) : '';
};

export const isHotelDemoCard = (cardId, cards = hotelDemoCards()) => {
    const id = (cardId || '').trim().toLowerCase();
    return !!id && cards.some((c) => String(c).trim().toLowerCase() === id);
};

/** Is the Command Center currently showing the hotel DEMO org? */
export const isHotelDemoCommandCenter = (search = '') => {
    try {
        const org = new URLSearchParams(search || '').get('org');
        if (org) return org === HOTEL_DEMO_ORG_ID;
        // No ?org= — fall back to who is signed in on this tab. The demo manager
        // session is scoped to the demo org; a real hotel admin never is.
        const raw = getStoredUser();
        if (!raw) return false;
        const u = JSON.parse(raw);
        const ids = u.organization_ids || (u.organization_id ? [u.organization_id] : []);
        return ids.includes(HOTEL_DEMO_ORG_ID);
    } catch (_e) { return false; }
};
